import { cn } from 'helpers/classnames';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import ProductUtils, { getAfterPayProps } from 'helpers/ProductUtils';
import useMartyContext from 'hooks/useMartyContext';
import type { ProductStyle } from 'types/cloudCatalog';
import FinalSale from 'components/common/FinalSale';
import { useFeaturePdpEnhancements } from 'hooks/unleash/useFeaturePdpEnhancements';
import AfterPay from 'components/productdetail/AfterPay';

import css from 'styles/components/productdetail/price.scss';
interface Props {
  productStyle: ProductStyle;
  percentOffText: string;
  showPercentOffBanner: boolean;
  className?: string;
  small?: boolean;
  defaultProductType?: string;
  isAvailable?: boolean;
  isLowestRecentPrice?: boolean;
}

export const Price = ({
  className,
  productStyle,
  small,
  defaultProductType,
  isAvailable,
  isLowestRecentPrice,
  percentOffText,
  showPercentOffBanner
}: Props) => {
  const {
    testId,
    marketplace: {
      checkout: { allowAfterPay }
    }
  } = useMartyContext();
  const { isTreatmentPricingEnabled } = useFeaturePdpEnhancements();
  const { productUrl, finalSale, price, originalPrice } = productStyle;
  const showDiscount = ProductUtils.isStyleOnSale(productStyle);
  const { priceDollarValue, priceCentsValue, isProductAfterpayEligible, components } = getAfterPayProps({
    price,
    originalPrice,
    productType: defaultProductType ?? ''
  });

  const mainPrice = price || originalPrice;
  const dollarSign = components ? '$' : '';
  const structuredDataPrice = priceDollarValue + (priceCentsValue ? `.${priceCentsValue}` : '');
  const percentOff = ProductUtils.getPercentOff(productStyle);
  const shouldShowPercentOff = showPercentOffBanner;
  const shouldShowTags = (showDiscount && isLowestRecentPrice) || (showPercentOffBanner && percentOff > 0);
  const shouldShowLowestPriceIn30Days = isLowestRecentPrice;

  return (
    <div className={cn(className, { [css.small]: small }, css.main)}>
      <div
        className={cn(css.container, 'gap-y-1', { ['gap-y-0']: isTreatmentPricingEnabled })}
        itemProp="offers"
        itemScope
        itemType="https://schema.org/Offer"
        data-test-id={testId('priceOffersContainer')}
      >
        {!isTreatmentPricingEnabled && shouldShowTags && (
          <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
            {shouldShowLowestPriceIn30Days && (
              <span data-test-id={testId('lowestPrice30Days')} className="font-semibold text-accent lumon:text-red-350">
                Lowest Price in 30 days
              </span>
            )}
          </div>
        )}
        {/* aria-label tells price to screen reader */}
        <span
          aria-label={mainPrice}
          className={cn(css.price, {
            [css.sale]: showDiscount,
            ['not-lumon:max-md:!text-2xl !mb-0 !inline-flex']: isTreatmentPricingEnabled
          })}
          data-test-id={testId('pdpProductPrice')}
          itemProp="price"
          content={structuredDataPrice}
        >
          {/*
            hide the fancy-formatted price (superscript dollar sign and cents)
            because the styling alone causes it to be broken up. a screen
            reader will see "$\n24\n.\n99" for a price of "$24.99"
          */}
          <span aria-hidden="true">
            {/* TODO refactor all this into a StylizedPrice component (working name) */}
            <span className={css.dollarSign} itemProp="priceCurrency" content="USD">
              {dollarSign}
            </span>
            {isTreatmentPricingEnabled ? ProductUtils.removeTrailingZeroes(priceDollarValue ?? '') : priceDollarValue}
            {priceCentsValue && (
              <>
                <span className={css.priceDecimalPoint}>.</span>
                <span className={css.priceCentsValue}>{priceCentsValue}</span>
              </>
            )}
          </span>
          {showDiscount && (
            <span className={css.discount}>
              <span
                className={cn(css.originalPrice, isTreatmentPricingEnabled ? 'relative -top-[3px]' : '')}
                data-test-id={testId('pdpOriginalPrice')}
              >
                <span className={css.msrpLabel}>
                  <abbr title="Manufacturer's Suggested Retail Price">MSRP</abbr>:{' '}
                </span>
                <span className={cn(css.msrpText, css.lineThrough, 'lumon:text-gray-600')} data-test-id={testId('msrpPrice')}>
                  {isTreatmentPricingEnabled ? ProductUtils.removeTrailingZeroes(originalPrice ?? '') : originalPrice}
                </span>

                {shouldShowPercentOff && (
                  <span className="text-accent lumon:text-red-350" data-test-id={testId('percentOff')}>
                    {isTreatmentPricingEnabled ? `(${percentOff}% ${percentOffText})` : `${percentOff}% ${percentOffText}`}
                  </span>
                )}
              </span>
            </span>
          )}
          {finalSale && <FinalSale className={isTreatmentPricingEnabled ? 'relative -top-[5px] m-0 my-2 ml-2' : css.finalSale} />}
        </span>
        {isTreatmentPricingEnabled && shouldShowTags && (
          <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
            {shouldShowLowestPriceIn30Days && (
              <span data-test-id={testId('lowestPrice30Days')} className="font-medium text-accent lumon:text-red-350">
                Lowest Price in 30 days
              </span>
            )}
          </div>
        )}
        {isAvailable ? (
          <meta itemProp="availability" content="https://schema.org/InStock" />
        ) : (
          <meta itemProp="availability" content="https://schema.org/OutOfStock" />
        )}
        <meta itemProp="url" content={productUrl} />
      </div>

      {!!components && allowAfterPay && priceDollarValue && !isTreatmentPricingEnabled && (
        <AfterPay isProductAfterpayEligible={isProductAfterpayEligible} priceDollarValue={priceDollarValue} priceCentsValue={priceCentsValue} />
      )}
    </div>
  );
};

export default withErrorBoundary('Price', Price);
